/* Cómo se lee una visita del portón.
 *
 * Sin React y sin Supabase, para que todo lo que se puede decir mal se pruebe
 * en una tabla. Es la decisión de `shared/puerto.js` y de `llegadas.detectar`.
 *
 * ── EL GEMELO ESTÁ EN FLOTABOT ────────────────────────────────────────────
 *
 * `visitas.py` decide QUÉ se guarda y qué se le contesta a portería; esto
 * decide cómo se lee lo guardado. Los tres estados del aviso son los mismos y
 * tienen que seguir siéndolo: con un «no se pudo avisar» de un lado y un «no
 * se avisó» del otro, la misma fila diría dos cosas distintas según dónde se
 * la mire.
 */

/* La hora en UTC−3 FIJO y no la del navegador.
 *
 * Es la misma razón que en el fichaje del puerto: este dato se usa para hablar
 * de a qué hora llegó una persona, y con la hora local la misma visita diría
 * 09:12 acá y 13:12 para alguien que abra el hub desde otro huso — sin forma
 * de saber cuál es la real.
 */
export function hora(iso) {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

export function fecha(iso) {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}`;
}

/* El día de una visita en AAAA-MM-DD, en UTC−3.
 *
 * Arrastra el corte del día: 23:30 del 3 en Argentina son las 02:30 UTC del 4,
 * y agrupando por el día UTC esa visita saltaría a la mañana siguiente.
 */
export function dia(iso) {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/* ── LOS CUATRO ESTADOS DEL AVISO, Y NINGUNO SE PUEDE FUNDIR ──────────────
 *
 *   avisado      · salió y Meta lo entregó
 *   fallo        · había a quién y NO se pudo entregar — hay que llamar
 *   sin_destino  · el nombre no está en la lista del bot. NO es un error:
 *                  Igna lo pidió explícitamente así, «que no le llegue nada
 *                  pero que quede registrado»
 *   sin_a_quien  · no se dijo a quién venía a ver
 *
 * `fallo` y `sin_destino` se ven parecidos y mandan a hacer cosas opuestas:
 * uno es «llamala vos ya mismo» y el otro «esto está bien así». Dibujados
 * igual, o se sale a resolver algo que no está roto o se deja a alguien
 * esperando en la puerta.
 */
export function estadoAviso(v) {
  if (!v) return "sin_a_quien";
  if (v.aviso_ok) return "avisado";
  if (v.a_quien_numero) return "fallo";
  if (v.a_quien) return "sin_destino";
  return "sin_a_quien";
}

export const AVISO = {
  avisado: { texto: "Avisado", tono: "ok" },
  fallo: { texto: "No se pudo avisar", tono: "mal" },
  sin_destino: { texto: "No está en la lista", tono: "neutro" },
  sin_a_quien: { texto: "Sin destinatario", tono: "neutro" },
};

/* A quién viene a ver, como se lee.
 *
 * GANA EL NOMBRE CANÓNICO CUANDO RESOLVIÓ, y si no se muestra lo que escribió
 * portería. Mostrar sólo el resuelto dejaría en blanco justo las visitas a
 * gente que no está en la planilla — que son las que hay que poder rastrear
 * después, porque de ésas nadie recibió un aviso.
 */
export function aQuien(v) {
  return (v?.a_quien_nombre || v?.a_quien || "").trim();
}

/* Cuántas hay que mirar: las que no se pudieron avisar.
 *
 * `sin_destino` NO cuenta. Es la distinción que sostiene el número: contar ahí
 * las visitas a gente que no está en la planilla convertiría lo normal en un
 * pendiente, y a la semana el contador se deja de mirar. Es la regla de la
 * columna «Fotos» de los trabajos del puerto.
 */
export function pendientes(filas) {
  return (filas || []).filter((v) => estadoAviso(v) === "fallo").length;
}

/* Agrupadas por día, el más nuevo primero.
 *
 * La pantalla se abre para ver el día de hoy, pero el rango puede traer
 * varios: sin el corte por día, dos visitas de las 9 de la mañana de días
 * distintos se leen como si fueran de la misma jornada.
 */
export function porDia(filas) {
  const mapa = new Map();
  for (const v of filas || []) {
    const d = dia(v.ingreso_en);
    if (!mapa.has(d)) mapa.set(d, []);
    mapa.get(d).push(v);
  }
  return [...mapa.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([d, vs]) => ({ dia: d, visitas: vs }));
}
